import type { PackageStatus } from "~/utils/allocationPackageLifecycle"
import { DateFormatType, formatDate } from "~/utils/datetime"
import { formatList, pluralize } from "~/utils/formatting"
import type { PackageFolder, RangePackage } from "./types"

export const PACKAGE_STATUS_META: Record<
  PackageStatus,
  { label: string; variant: "green" | "blue" | "neutral" }
> = {
  active: { label: "Active", variant: "green" },
  upcoming: { label: "Upcoming", variant: "blue" },
  ended: { label: "Inactive", variant: "neutral" },
  not_active: { label: "Inactive", variant: "neutral" },
}

export const PACKAGE_TYPE_LABEL: Record<RangePackage["type"], string> = {
  seasonal: "Seasonal",
  noos: "NOOS",
  clearance: "Clearance",
}

export const PACKAGE_TYPE_BADGE = {
  noos: "emphasizedBlue",
  seasonal: "violet",
  clearance: "neutral",
} as const satisfies Record<RangePackage["type"], string>

const dayMonth = (date: string) => formatDate(date, DateFormatType.DayMonth)

/** The rail's short window: "05 Nov → 28 Feb", "Always open" for NOOS, else "No dates yet". */
export function railWindowLabel(pkg: Pick<RangePackage, "type" | "startDate" | "cutoffDate">) {
  if (pkg.type === "noos") return "Always open"
  if (pkg.startDate === null || pkg.cutoffDate === null) return "No dates yet"
  return `${dayMonth(pkg.startDate)} → ${dayMonth(pkg.cutoffDate)}`
}

/** "12 stores · from folder" or "· own stores"; a package outside a folder only has its own. */
export function storesLabel(pkg: Pick<RangePackage, "storeIds" | "storesFolderId" | "folderId">) {
  const count = `${pkg.storeIds.length} ${pkg.storeIds.length === 1 ? "store" : "stores"}`
  if (pkg.folderId === null) return count
  return `${count} · ${pkg.storesFolderId === null ? "own stores" : "from folder"}`
}

export const isPackageOpen = (status: PackageStatus) => status === "active" || status === "upcoming"

const FOLDER_NOT_ACTIVE_REASON = "This folder is archived, so it can't be changed."
export const FOLDER_DELETING_REASON = "This folder is marked for deletion. Keep it to change it."

/** Why a folder's page and menu can't change it, or undefined. */
export function folderDisabledReason(
  folder: Pick<PackageFolder, "deactivated">,
  deleting: boolean,
) {
  if (deleting) return FOLDER_DELETING_REASON
  return folder.deactivated ? FOLDER_NOT_ACTIVE_REASON : undefined
}

export const formatPackageDate = (date: string) => formatDate(date, DateFormatType.FullDateShort)

export const packageWindowLabel = (
  startDate: string | null,
  cutoffDate: string | null,
  undatedLabel = "No dates",
) =>
  startDate === null || cutoffDate === null
    ? undatedLabel
    : `${formatPackageDate(startDate)} to ${formatPackageDate(cutoffDate)}`

export const formatDayShift = (days: number) =>
  `${days > 0 ? "+" : "−"}${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"}`

/** Rename… focuses the name with its text selected. */
export function focusAndSelect(ref: { current: HTMLInputElement | null }) {
  requestAnimationFrame(() => {
    ref.current?.focus()
    ref.current?.select()
  })
}

export const OVERLAP_TITLE = "Overlap with another package."
export const OVERLAP_ADVICE =
  "Remove the variants from one package or change the stores. Where both stay, the most restrictive rule wins."

const OTHERS_NAMED = 2

/** Open issues on a package; an overlap is one issue whatever its pair count. */
export const packageIssueCount = (pkg: RangePackage) => (pkg.overlap ? 1 : 0)

export function overlapSummary(overlap: NonNullable<RangePackage["overlap"]>) {
  const pairs = overlap.pairCount
  const named = overlap.others.slice(0, OTHERS_NAMED).map((other) => other.name)
  const rest = overlap.others.length - named.length
  const packages = formatList(rest > 0 ? [...named, pluralize(rest, "more package")] : named)
  return `${pairs.toLocaleString("en-US")} variant × store ${pairs === 1 ? "pair is" : "pairs are"} also in ${packages}.`
}

export const overlapTooltip = (overlap: NonNullable<RangePackage["overlap"]>, advice = false) =>
  `${OVERLAP_TITLE}\n${overlapSummary(overlap)}${advice ? ` ${OVERLAP_ADVICE}` : ""}`
